import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { sidebarConfig } from '../routes/utils/sidebarConfig';
import { FaUniversity, FaChevronDown, FaUserCircle } from "react-icons/fa";
import Cookies from 'js-cookie';
import ThemeToggleButton from '../context/ThemeToggleButton'; // Corrected import path

export default function Sidebar({ role = 'admin' }) {
  const location = useLocation();
  const currentSidebarData = sidebarConfig[role] || { sections: [] };

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const roleFromCookie = Cookies.get('userRole');

  const getDisplayName = (userObj) => {
    if (!userObj) return "Guest";
    return userObj.name || userObj.username || `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || "User";
  };

  const username = getDisplayName(user);
  const userRole = roleFromCookie ? (roleFromCookie.charAt(0).toUpperCase() + roleFromCookie.slice(1)) : (user.role || "User");

  const [openSections, setOpenSections] = useState({});
  const [activeSection, setActiveSection] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  useEffect(() => {
    const path = location.pathname;
    let activeFound = false;
    for (const section of currentSidebarData.sections || []) {
      if (section.items.some(item => path.startsWith(item.path))) {
        setActiveSection(section.id);
        setOpenSections(prev => ({ ...prev, [section.id]: true }));
        activeFound = true;
        break;
      }
    }
    if (!activeFound && path.includes('dashboard')) {
      setActiveSection('dashboard');
    }
  }, [location.pathname, role, currentSidebarData.sections]);

  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) setSidebarCollapsed(true);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleSidebarHover = (hovered) => {
    if (!isMobile) setSidebarHovered(hovered);
  };

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const isActive = (path) => location.pathname === path;
  const isExpanded = !sidebarCollapsed || sidebarHovered || (isMobile && mobileMenuOpen);

  const getSidebarWidth = () => {
    if (isMobile) return mobileMenuOpen ? 300 : 0;
    if (sidebarCollapsed && !sidebarHovered) return 70;
    return 300;
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // --- Sub-Components using CSS Variables ---

  const MenuItem = ({ path, icon: Icon, label, description, color }) => {
    const active = isActive(path);
    const itemHovered = hoveredItem === path;
    return (
      <Link
        to={path}
        onClick={closeMobileMenu}
        onMouseEnter={() => setHoveredItem(path)}
        onMouseLeave={() => setHoveredItem(null)}
        style={{ display: "block", textDecoration: "none", position: "relative" }}
      >
        <div style={{
          display: "flex", alignItems: "center", padding: isExpanded ? "12px 20px" : "16px 0",
          margin: isExpanded ? "4px 12px" : "4px 8px", borderRadius: isExpanded ? "16px" : "12px",
          backgroundColor: active ? "var(--item-active-bg)" : (itemHovered ? "var(--item-hover-bg)" : "transparent"),
          justifyContent: isExpanded ? "flex-start" : "center",
          color: "var(--text-primary)"
        }}>
          <div style={{ width: "40px", height: "40px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", marginRight: isExpanded ? "12px" : "0" }}>
            <Icon size={18} style={{ color: active ? (color || "var(--brand-gradient-start)") : (itemHovered ? (color || "var(--brand-gradient-start)") : "var(--icon-color)") }} />
          </div>
          {isExpanded && (
            <div>
              <div style={{ fontSize: "15px", fontWeight: "600" }}>{label}</div>
              {description && <div style={{ fontSize: "11px", color: "var(--text-tertiary)" }}>{description}</div>}
            </div>
          )}
        </div>
        {!isExpanded && itemHovered && (
          <div style={{ position: "fixed", left: "78px", background: "var(--tooltip-bg)", color: "var(--tooltip-text)", padding: "12px 16px", borderRadius: "12px", zIndex: 10000 }}>
            <div style={{ fontWeight: "700" }}>{label}</div>
            {description && <div style={{ fontSize: "11px", opacity: 0.8 }}>{description}</div>}
          </div>
        )}
      </Link>
    );
  };

  const SectionHeader = ({ icon: Icon, label, isOpen, onClick, itemCount, color, section }) => {
    const isActiveSection = activeSection === section;
    const itemHovered = hoveredItem === `section-${label}`;
    return (
      <div
        onClick={onClick}
        onMouseEnter={() => setHoveredItem(`section-${label}`)}
        onMouseLeave={() => setHoveredItem(null)}
        style={{
          display: "flex", alignItems: "center", padding: isExpanded ? "16px 20px" : "16px 0",
          margin: isExpanded ? "8px 12px" : "8px 8px", borderRadius: "16px", cursor: "pointer",
          backgroundColor: isActiveSection ? 'var(--item-active-bg)' : (itemHovered ? "var(--item-hover-bg)" : "transparent"),
          justifyContent: isExpanded ? "space-between" : "center",
          color: "var(--text-primary)"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
          <div style={{ width: "44px", height: "44px", borderRadius: "14px", background: `linear-gradient(135deg, ${color}15, ${color}25)`, display: "flex", alignItems: "center", justifyContent: "center", marginRight: isExpanded ? "16px" : "0" }}>
            <Icon size={20} style={{ color: color }} />
          </div>
          {isExpanded && (
            <div>
              <div style={{ fontSize: "16px", fontWeight: "700" }}>{label}</div>
              <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{itemCount} items</div>
            </div>
          )}
        </div>
        {isExpanded && (
          <div style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s ease" }}>
            <FaChevronDown size={12} color={isActiveSection ? color : "var(--icon-color)"} />
          </div>
        )}
      </div>
    );
  };

  const SubMenu = ({ isOpen, children }) => (
    <div style={{ maxHeight: isOpen ? "2000px" : "0", overflow: 'hidden', transition: 'max-height 0.4s ease' }}>
      {children}
    </div>
  );

  return (
    <>
      <div
        onMouseEnter={() => handleSidebarHover(true)}
        onMouseLeave={() => handleSidebarHover(false)}
        style={{
          position: "fixed", top: 0, left: 0, height: "100vh",
          width: `${getSidebarWidth()}px`,
          background: "var(--sidebar-bg)",
          borderRight: "1px solid var(--sidebar-border)",
          transition: "width 0.4s ease, background-color 0.3s ease, border-color 0.3s ease",
          zIndex: 1010, display: "flex", flexDirection: "column"
        }}
      >
        <div style={{ padding: "24px 20px", borderBottom: "1px solid var(--sidebar-border)", display: 'flex', alignItems: 'center' }}>
          <div style={{ width: "48px", height: "48px", background: `linear-gradient(135deg, var(--brand-gradient-start), var(--brand-gradient-end))`, borderRadius: "16px", display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '22px' }}>
            <FaUniversity />
          </div>
          {isExpanded && (
            <div style={{ marginLeft: '16px' }}>
              <div style={{ fontSize: "22px", fontWeight: "800", color: "var(--text-primary)" }}>SCHOOL ERP</div>
            </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 0" }}>
          {currentSidebarData.dashboard && <MenuItem {...currentSidebarData.dashboard} />}
          {(currentSidebarData.sections || []).map(section => (
            <React.Fragment key={section.id}>
              <SectionHeader {...section} isOpen={!!openSections[section.id]} onClick={() => toggleSection(section.id)} itemCount={section.items.length} section={section.id} />
              <SubMenu isOpen={!!openSections[section.id]}>
                {section.items.map((item) => <MenuItem key={item.path} {...item} />)}
              </SubMenu>
            </React.Fragment>
          ))}
        </div>

        <div style={{ padding: '20px', borderTop: '1px solid var(--sidebar-border)' }}>
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: isExpanded ? 'space-between' : 'center',
            flexDirection: isExpanded ? 'row' : 'column',
            gap: isExpanded ? '0' : '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isExpanded ? '12px' : '0' }}>
              <FaUserCircle size={isExpanded ? 40 : 28} color="var(--icon-color)" />
              {isExpanded && (
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ fontWeight: '600', color: "var(--text-primary)", whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{username}</div>
                  <div style={{ fontSize: '12px', color: "var(--text-secondary)" }}>{userRole}</div>
                </div>
              )}
            </div>
            <ThemeToggleButton />
          </div>
        </div>
      </div>
    </>
  );
}

